import type { ValueOf } from 'ts-essentials'
import { IdentityInitiationIntent } from '@/services/identity-types'
import gtm from '@/lib/gtm'
import marketing from '@/lib/marketing'

import { getUserProfile } from '@/services/identity-client'
/* eslint-disable-next-line no-restricted-imports */
import type { SSOProvider } from '@onxmaps/identity-client'

/** Events specific to user intent */
export const identityInitiationEvent = {
  [IdentityInitiationIntent.Login]: 'login_initiated',
  [IdentityInitiationIntent.CreateAccount]: 'create_account_initiated',
  [IdentityInitiationIntent.SSO]: 'sso_auth_initiated',
} as const satisfies Record<IdentityInitiationIntent, string>

/** Initiation event used when there is no explicit user intent provided */
const DEFAULT_INITIATION_EVENT = 'identity_page_viewed'

/** Possible results of identity interaction */
export enum IdentityResult {
  Success = 'success',
  Dismiss = 'dismiss',
}

/** Maps identity results to their appropriate events */
export const identityResultEvent = {
  [IdentityResult.Success]: 'identity_auth_successful',
  [IdentityResult.Dismiss]: 'identity_dismissed',
} as const

export type IdentityInitiationMarketingBaseProps = {
  origin?: string
  share_id?: string
}

export type IdentityInitiationMarketingProps =
  IdentityInitiationMarketingBaseProps & {
    sso_provider?: SSOProvider
  }

const logIdentityEvent = (
  event:
    | ValueOf<typeof identityResultEvent | typeof identityInitiationEvent>
    | typeof DEFAULT_INITIATION_EVENT,
  properties?: IdentityInitiationMarketingProps
) => {
  if (properties) {
    marketing.logEvent(event, properties)
    gtm.logEvent({ event, ...properties })
  } else {
    marketing.logEvent(event)
    gtm.logEvent({ event })
  }
}

/** Publish an event for sending the user to identity via redirect or pop up flow */
export const logIdentityInitiation = (
  intent?: IdentityInitiationIntent,
  marketingProperties?: IdentityInitiationMarketingProps
) => {
  const marketingEvent = intent
    ? identityInitiationEvent[intent]
    : DEFAULT_INITIATION_EVENT
  logIdentityEvent(marketingEvent, marketingProperties)
}

/**
 * Publish an event for the result of the user's interaction with identity
 * If a resolution event was stored and the interaction was successful, then the
 * resolution will also attempt to publish.
 */
export const logIdentityResult = (result: IdentityResult) => {
  logIdentityEvent(identityResultEvent[result])
  removeResolutionEvent()
}

/**
 * Identity Resolution Events
 *
 * This uses session storage to note that a user came into the app via the identity
 * callback route, and then resolves to send proper events once in a vertical.
 */

/** Resolution event storage key(s) */
export enum ResolutionKey {
  Event = 'resolution.event',
}

/** Helper to store a user intent in session storage */
export const storeResolutionEvent = () => {
  window.sessionStorage.setItem(ResolutionKey.Event, '1')
}

/** Removes a stored resolution event, if it exists */
export const removeResolutionEvent = () => {
  window.sessionStorage.removeItem(ResolutionKey.Event)
}

const hasUnresolvedIdentityInteraction = () => {
  try {
    return window.sessionStorage.getItem(ResolutionKey.Event) === '1'
  } catch (e) {
    return false
  }
}

// Max age of account to consider an "account creates" at time of auth
export const CREATE_ACCOUNT_WINDOW = 5 * 60 * 1000

/**
 * checks if the account is newer than the account create window, to create
 * a best approximation for "account create" event.
 */
export const isInCreateAccountWindow = async () => {
  const profile = await getUserProfile()
  if (profile && profile.joined_at && typeof profile.joined_at === 'number') {
    // joined_at property is in whole seconds, date.now() is in milliseconds
    const joinedAtMs = profile.joined_at * 1000
    if (Date.now() - joinedAtMs < CREATE_ACCOUNT_WINDOW) {
      return true
    }
  }
  return false
}

/**
 * we send account create events if the account is within the account window.
 * although technically this account wasn't created within the vertical, for
 * marketing attribution purposes we are sending this event.
 */
export const resolveIdentityInteraction = async () => {
  if (hasUnresolvedIdentityInteraction()) {
    // check if a create account, log appropriately
    if (await isInCreateAccountWindow()) {
      marketing.accountCreated()
      gtm.accountCreated('fish')
    }
    logIdentityResult(IdentityResult.Success)
  }
}
